import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { getErrorMessage } from '@/hooks/useApi';

import {
  buildNodeTree,
  findRootNode,
  FreeTier,
  StoryNode,
  useBatchUpdateFreeTier,
  useStoryNodes,
  useUpdateNodeFreeTier,
} from './useNodes';

const TIER_OPTIONS: Array<{ value: FreeTier | null; label: string; description: string }> = [
  { value: null, label: 'None', description: 'Not free (requires tokens)' },
  { value: 'all', label: 'All Users', description: 'Free for everyone' },
  { value: 'narrator', label: 'Narrator+', description: 'Free for Narrator tier and above' },
  {
    value: 'storyteller',
    label: 'Storyteller+',
    description: 'Free for Storyteller tier and above',
  },
  { value: 'trailblazer', label: 'Trailblazer', description: 'Free for Trailblazer tier only' },
];

export function NodePathEditor() {
  const { storyId } = useParams<{ storyId: string }>();
  const { data: nodes, isLoading, error } = useStoryNodes(storyId);
  const updateFreeTier = useUpdateNodeFreeTier();
  const batchUpdateFreeTier = useBatchUpdateFreeTier();

  // Navigation state
  const rootNode = nodes ? findRootNode(nodes) : undefined;
  const [path, setPath] = useState<StoryNode[]>([]);
  const [selectedTier, setSelectedTier] = useState<FreeTier | null>(null);

  // Initialize path with root node when nodes load
  const effectivePath = path.length > 0 ? path : rootNode ? [rootNode] : [];

  // Get current node (last in path or root)
  const currentNode = effectivePath.length > 0 ? effectivePath[effectivePath.length - 1] : rootNode;
  const nodeTree = nodes ? buildNodeTree(nodes) : new Map();

  // Get children of current node
  const children = currentNode?.nodeId ? (nodeTree.get(currentNode.nodeId) ?? []) : [];

  const handleSelectChoice = (childNode: StoryNode) => {
    setPath([...effectivePath, childNode]);
  };

  const handleNavigateToPath = (index: number) => {
    setPath(effectivePath.slice(0, index + 1));
  };

  const handleUpdateSingleNode = async (nodeId: string, tier: FreeTier | null) => {
    try {
      await updateFreeTier.mutateAsync({ nodeId, freeTier: tier });
    } catch (err) {
      alert(getErrorMessage(err));
    }
  };

  const handleMarkEntirePath = async () => {
    if (effectivePath.length === 0) return;

    const nodeIds = effectivePath.map((n) => n.nodeId);
    try {
      const result = await batchUpdateFreeTier.mutateAsync({ nodeIds, freeTier: selectedTier });
      alert(`Updated ${result.updatedCount} of ${result.requestedCount} nodes`);
    } catch (err) {
      alert(getErrorMessage(err));
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent mx-auto"></div>
        <p className="mt-4 text-slate-600">Loading nodes...</p>
      </div>
    );
  }

  if (error) {
    return <div className="rounded-lg bg-red-50 p-4 text-red-700">{getErrorMessage(error)}</div>;
  }

  if (!nodes || nodes.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-600">No nodes found for this story</p>
        <Link to={`/stories/${storyId}`} className="mt-4 text-indigo-600 hover:text-indigo-800">
          Back to story
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <div className="mb-6">
        <Link to={`/stories/${storyId}`} className="text-indigo-600 hover:text-indigo-800">
          &larr; Back to Story
        </Link>
      </div>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900">Manage Free Content</h1>
        <p className="mt-2 text-slate-600">
          Navigate through the story tree and mark nodes as free for specific subscription tiers.
        </p>
      </div>

      {/* Path breadcrumb */}
      <div className="mb-6 rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-medium text-slate-500 mb-2">Current Path</h2>
        <div className="flex flex-wrap items-center gap-2">
          {effectivePath.map((node, index) => (
            <span key={node.nodeId} className="flex items-center">
              {index > 0 && <span className="text-slate-400 mx-2">&rarr;</span>}
              <button
                onClick={() => handleNavigateToPath(index)}
                className={`px-3 py-1 rounded-lg text-sm ${
                  index === effectivePath.length - 1
                    ? 'bg-indigo-100 text-indigo-700 font-medium'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Ch. {node.storyState?.chapterNumber ?? node.depth + 1}
                {node.freeTier && (
                  <span className="ml-1 text-xs">
                    ({node.freeTier === 'all' ? 'Free' : node.freeTier})
                  </span>
                )}
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Mark path controls */}
      <div className="mb-8 rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-medium text-slate-500 mb-3">Mark Entire Path</h2>
        <div className="flex items-center gap-4">
          <select
            value={selectedTier ?? ''}
            onChange={(e) => setSelectedTier((e.target.value || null) as FreeTier | null)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            {TIER_OPTIONS.map((option) => (
              <option key={option.label} value={option.value ?? ''}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            onClick={handleMarkEntirePath}
            disabled={batchUpdateFreeTier.isPending || effectivePath.length === 0}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700 disabled:bg-indigo-400"
          >
            {batchUpdateFreeTier.isPending
              ? 'Updating...'
              : `Apply to ${effectivePath.length} Nodes`}
          </button>
        </div>
      </div>

      {/* Current node details */}
      {currentNode && (
        <div className="mb-8 rounded-lg border border-slate-200 bg-white p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Chapter {currentNode.storyState?.chapterNumber ?? currentNode.depth + 1}
                {currentNode.storyState?.chapterTitle && (
                  <span className="ml-2 text-slate-500 font-normal">
                    - {currentNode.storyState.chapterTitle}
                  </span>
                )}
              </h2>
              <p className="text-sm text-slate-500 mt-1">Node: {currentNode.nodeId}</p>
            </div>
            <NodeStatusBadge status={currentNode.status} />
          </div>

          {/* Free tier selector for current node */}
          <div className="mt-4 pt-4 border-t border-slate-200">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Free Tier for this node:
            </label>
            <div className="flex items-center gap-3">
              <select
                value={currentNode.freeTier ?? ''}
                onChange={(e) =>
                  handleUpdateSingleNode(
                    currentNode.nodeId,
                    (e.target.value || null) as FreeTier | null
                  )
                }
                disabled={updateFreeTier.isPending}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                {TIER_OPTIONS.map((option) => (
                  <option key={option.label} value={option.value ?? ''}>
                    {option.label} - {option.description}
                  </option>
                ))}
              </select>
              {updateFreeTier.isPending && (
                <span className="text-sm text-slate-500">Saving...</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Choices / Children */}
      {currentNode && !currentNode.isEnding && (
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Choices</h2>
          {children.length === 0 ? (
            <p className="text-slate-500">
              {currentNode.status === 'generating'
                ? 'Node is still being generated...'
                : 'No child nodes available'}
            </p>
          ) : (
            <div className="space-y-4">
              {children.map((child: StoryNode, index: number) => (
                <ChoiceCard
                  key={child.nodeId}
                  node={child}
                  choiceText={currentNode.choices?.[index]?.text ?? `Choice ${index + 1}`}
                  onSelect={() => handleSelectChoice(child)}
                  onUpdateFreeTier={(tier) => handleUpdateSingleNode(child.nodeId, tier)}
                  isUpdating={updateFreeTier.isPending}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Ending indicator */}
      {currentNode?.isEnding && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-6 text-center">
          <p className="text-green-700 font-medium">This is a story ending</p>
        </div>
      )}
    </div>
  );
}

function NodeStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    generating: 'bg-yellow-100 text-yellow-700',
    ready: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
  };

  return (
    <span
      className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${colors[status] || 'bg-slate-100 text-slate-700'}`}
    >
      {status}
    </span>
  );
}

interface ChoiceCardProps {
  node: StoryNode;
  choiceText: string;
  onSelect: () => void;
  onUpdateFreeTier: (tier: FreeTier | null) => void;
  isUpdating: boolean;
}

function ChoiceCard({ node, choiceText, onSelect, onUpdateFreeTier, isUpdating }: ChoiceCardProps) {
  return (
    <div className="rounded-lg border border-slate-200 p-4 hover:border-indigo-300 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <button
            onClick={onSelect}
            disabled={node.status !== 'ready'}
            className="text-left w-full"
          >
            <p className="font-medium text-slate-900">{choiceText}</p>
            <p className="text-sm text-slate-500 mt-1">
              {node.status === 'ready'
                ? `Chapter ${node.storyState?.chapterNumber ?? node.depth + 1}`
                : node.status === 'generating'
                  ? 'Generating...'
                  : 'Failed'}
            </p>
          </button>
        </div>
        <div className="flex items-center gap-2 ml-4">
          <FreeTierBadge tier={node.freeTier} />
          <select
            value={node.freeTier ?? ''}
            onChange={(e) => onUpdateFreeTier((e.target.value || null) as FreeTier | null)}
            disabled={isUpdating}
            className="rounded border border-slate-200 px-2 py-1 text-xs"
            onClick={(e) => e.stopPropagation()}
          >
            <option value="">None</option>
            <option value="all">All</option>
            <option value="narrator">Narrator+</option>
            <option value="storyteller">Storyteller+</option>
            <option value="trailblazer">Trailblazer</option>
          </select>
          <button
            onClick={onSelect}
            disabled={node.status !== 'ready'}
            className="rounded-lg bg-indigo-600 px-3 py-1 text-sm text-white hover:bg-indigo-700 disabled:bg-slate-300"
          >
            Go &rarr;
          </button>
        </div>
      </div>
    </div>
  );
}

function FreeTierBadge({ tier }: { tier?: FreeTier }) {
  if (!tier) return null;

  const colors: Record<FreeTier, string> = {
    all: 'bg-green-100 text-green-700',
    narrator: 'bg-blue-100 text-blue-700',
    storyteller: 'bg-purple-100 text-purple-700',
    trailblazer: 'bg-amber-100 text-amber-700',
  };

  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${colors[tier]}`}>
      {tier === 'all' ? 'Free' : tier}
    </span>
  );
}
